import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTheme } from "../../providers/ThemeContext";
import { useI18n } from "../../languages/providers/I18nProvider";
import { useLangPath } from "../../hooks/useLangPath";

/**
 * Real 404 page. Replaces the old catch-all redirect to /en, which Google
 * treated as a "soft 404" and which hid broken links.
 * Place at: src/pages/notFound/NotFoundPage.tsx
 */

const COPY = {
  en: {
    title: "Page not found | Creation Arts",
    code: "404",
    heading: "This page doesn't exist",
    text: "The link may be old or mistyped. These pages are a good place to continue:",
    home: "Home",
    services: "Our services",
    work: "Our work",
    contact: "Contact us",
  },
  ar: {
    title: "الصفحة غير موجودة | فنون الإبداع",
    code: "404",
    heading: "هذه الصفحة غير موجودة",
    text: "ربما الرابط قديم أو مكتوب بشكل خاطئ. يمكنك المتابعة من هنا:",
    home: "الرئيسية",
    services: "خدماتنا",
    work: "أعمالنا",
    contact: "تواصل معنا",
  },
} as const;

export default function NotFoundPage() {
  const { theme } = useTheme();
  const { lang } = useI18n();
  const lp = useLangPath();
  const c = COPY[lang === "ar" ? "ar" : "en"];

  const links: [string, string][] = [
    [c.home, lp("")],
    [c.services, lp("/services")],
    [c.work, lp("/ourwork")],
    [c.contact, lp("/contact")],
  ];

  return (
    <>
      <Helmet>
        <title>{c.title}</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <main
        dir={lang === "ar" ? "rtl" : "ltr"}
        style={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          padding: "64px 16px",
          textAlign: "center",
          color: theme.text,
        }}
      >
        <div style={{ maxWidth: 560 }}>
          <p
            style={{
              fontSize: "clamp(56px, 12vw, 96px)",
              fontWeight: 800,
              lineHeight: 1,
              margin: 0,
              opacity: 0.15,
            }}
            aria-hidden="true"
          >
            {c.code}
          </p>
          <h1
            style={{ margin: "8px 0 12px", fontSize: "clamp(24px, 4vw, 34px)" }}
          >
            {c.heading}
          </h1>
          <p style={{ margin: "0 0 28px", opacity: 0.8, lineHeight: 1.6 }}>
            {c.text}
          </p>
          <nav
            aria-label={c.heading}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "center",
            }}
          >
            {links.map(([label, to]) => (
              <Link
                key={to}
                to={to}
                style={{
                  padding: "10px 18px",
                  border: `1px solid ${theme.text}`,
                  borderRadius: 8,
                  color: "inherit",
                  textDecoration: "none",
                  fontWeight: 500,
                }}
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </main>
    </>
  );
}
